// Dialog component for creating new rubrics or editing existing ones
// Provides a form interface for rubric name and questions with point values

import { useState, useEffect } from 'react';
import { createRubric, updateRubric } from '../../../services/rubric.service';
import type { Rubric, RubricQuestion } from '../../../types/rubric.types';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';

// TypeScript interface for component props
type Props = {
  open: boolean; // Whether the dialog is visible
  onClose: () => void; // Callback when dialog is closed
  onSaved: (savedRubric?: Rubric) => void; // Callback when rubric is saved successfully
  initialRubric?: Rubric; // Existing rubric data for editing (undefined = new rubric)
  fairId?: string; // Fair ID for new rubrics
};

/**
 * RubricEditorDialog - Modal dialog for creating/editing rubrics
 * Handles both new rubric creation and existing rubric editing
 */
export default function RubricEditorDialog({
  open,
  onClose,
  onSaved,
  initialRubric,
  fairId,
}: Props) {
  const { authStatus } = useAuth();
  // State for the rubric name
  const [name, setName] = useState('');

  // State for the array of questions
  const [questions, setQuestions] = useState<RubricQuestion[]>([]);

  // Initialize form data when dialog opens or initialRubric changes
  useEffect(() => {
    if (!open) return; // Don't update if dialog is closed

    if (initialRubric) {
      // Editing existing rubric - populate form with current data
      setName(initialRubric.name);
      setQuestions(initialRubric.questions);
    } else {
      // Creating new rubric - reset form to empty state
      setName('');
      setQuestions([]);
    }
  }, [initialRubric, open]);

  // Don't render anything if dialog is closed
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card
        className="w-full max-w-2xl p-4 space-y-4"
        style={{ maxHeight: '80vh', overflowY: 'auto' }} // Make scrollable for long rubrics
      >
        {/* Dialog title - changes based on create/edit mode */}
        <div className="text-lg font-semibold">
          {initialRubric ? 'Edit Rubric' : 'New Rubric'}
        </div>

        {/* Rubric name input */}
        <input
          className="w-full rounded border px-3 py-2"
          placeholder="Rubric Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        {/* Questions list with add/remove functionality */}
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={i} className="flex items-center gap-3">
              {/* Question text input */}
              <textarea
                className="flex-1 rounded border px-3 py-2 resize-none"
                placeholder="Question"
                value={q.question}
                rows={2}
                onChange={(e) => {
                  // Create new array with updated question
                  const next = [...questions];
                  next[i] = { ...next[i], question: e.target.value };
                  setQuestions(next);
                }}
              />

              {/* Max points input */}
              <input
                type="number"
                min={0}
                className="w-28 rounded border px-3 py-2"
                placeholder="Max Points"
                value={q.maxPoints}
                onChange={(e) => {
                  // Create new array with updated maxPoints
                  const next = [...questions];
                  next[i] = { ...next[i], maxPoints: Number(e.target.value) };
                  setQuestions(next);
                }}
              />

              {/* Remove question button */}
              <Button
                variant="secondary"
                onClick={() =>
                  setQuestions(questions.filter((_, idx) => idx !== i))
                }
              >
                Remove
              </Button>
            </div>
          ))}

          {/* Add new question button */}
          <Button
            variant="secondary"
            onClick={() =>
              setQuestions([
                ...questions,
                {
                  question: '',
                  maxPoints: 10,
                  id: crypto.randomUUID(), // Generate unique ID for new question
                },
              ])
            }
          >
            Add Question
          </Button>
        </div>

        {/* Dialog action buttons */}
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                const totalPoints = questions.reduce(
                  (sum, q) => sum + q.maxPoints,
                  0
                );

                if (initialRubric) {
                  // Editing existing rubric - update in database
                  await updateRubric(initialRubric.id, { name, questions });

                  // Return the updated rubric
                  const updatedRubric: Rubric = {
                    ...initialRubric,
                    name,
                    questions,
                    totalPoints,
                    updatedAt: Date.now(),
                  };

                  onSaved(updatedRubric);
                } else {
                  // Creating new rubric - save to database
                  if (!fairId) {
                    alert('Fair ID is required to create a rubric');
                    return;
                  }
                  if (!authStatus.user?.uid) {
                    alert('User must be logged in');
                    return;
                  }
                  const rubricId = await createRubric(
                    name,
                    fairId,
                    authStatus.user.uid,
                    questions
                  );

                  // Return the newly created rubric
                  const newRubric: Rubric = {
                    id: rubricId,
                    name,
                    fairId,
                    createdBy: authStatus.user.uid,
                    questions,
                    totalPoints,
                    isActive: true,
                    createdAt: Date.now(),
                    updatedAt: Date.now(),
                  };

                  onSaved(newRubric);
                }
              } catch (error: any) {
                alert(error.message || 'Failed to save rubric');
              }
            }}
            disabled={
              !name || questions.length === 0 || (!initialRubric && !fairId)
            } // Disable if form is invalid
          >
            Save
          </Button>
        </div>
      </Card>
    </div>
  );
}
